import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Zoom
} from '@mui/material';
// MUI 7's Grid ignores item/xs/sm/md, so these layouts collapsed to content width
// (dashboard on phones, ST-20261007-0001 follow-up). GridLegacy is the pre-v7 grid.
import Grid from '@mui/material/GridLegacy';
import { quickActions as actionConfig, dashboardTheme } from '../styles/dashboardTheme';
import WidgetInfoTooltip from './WidgetInfoTooltip';

/**
 * QuickActionsGrid Component
 * 2x2 grid of quick action buttons for main features
 */
const QuickActionsGrid = ({ onActionClick }) => {
  const [hoveredAction, setHoveredAction] = useState(null);
  const actions = actionConfig;

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: dashboardTheme.shadows.medium,
        transition: dashboardTheme.animations.transition,
        '&:hover': {
          boxShadow: dashboardTheme.shadows.large
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          Quick Actions
        </Typography>
        <WidgetInfoTooltip id="quickActions" />
      </Box>

      <Grid container spacing={2} sx={{ flex: 1 }}>
        {actions.map((action, index) => (
          <Grid item xs={6} key={action.id}>
            <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
              <Button
                fullWidth
                onMouseEnter={() => setHoveredAction(action.id)}
                onMouseLeave={() => setHoveredAction(null)}
                onClick={() => onActionClick(action.id)}
                sx={{
                  height: '100%',
                  minHeight: 100,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  borderRadius: '12px',
                  background: hoveredAction === action.id ? action.gradient : '#f8f9fa',
                  border: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: dashboardTheme.shadows.colored(action.color),
                    '& .action-icon': {
                      transform: 'scale(1.2) rotate(5deg)'
                    },
                    '& .action-label': {
                      color: '#ffffff'
                    },
                    '& .action-description': {
                      color: 'rgba(255,255,255,0.9)'
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: hoveredAction === action.id 
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 100%)'
                      : 'none',
                    pointerEvents: 'none'
                  }
                }}
              >
                <Box 
                  className="action-icon"
                  sx={{ 
                    fontSize: '2rem',
                    transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                  }}
                >
                  {action.icon}
                </Box>
                <Typography 
                  variant="body2" 
                  fontWeight="bold"
                  className="action-label"
                  sx={{ 
                    color: hoveredAction === action.id ? '#ffffff' : action.color,
                    transition: 'color 0.3s',
                    fontSize: '0.9rem'
                  }}
                >
                  {action.label}
                </Typography>
                <Typography 
                  variant="caption" 
                  className="action-description"
                  sx={{ 
                    color: hoveredAction === action.id ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                    transition: 'color 0.3s',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}
                >
                  {action.description}
                </Typography>
              </Button>
            </Zoom>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default QuickActionsGrid;